import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Loader2, Film, Download, Share2, Copy, Check, AlertCircle, Sparkles, X } from "lucide-react";
import api, { API, formatApiErrorDetail, getStoredToken } from "@/lib/api";
import { toast } from "sonner";

const MUSIC_OPTIONS = [
    { value: "wedding", label: "Wedding · soft" },
    { value: "festive", label: "Festive · upbeat" },
    { value: "romantic", label: "Romantic · gentle" },
    { value: "royal", label: "Royal · regal" },
    { value: "modern", label: "Modern · chill" },
    { value: "silent", label: "No music" },
];

export default function CollectionReelDialog({ open, onOpenChange, sarees = [] }) {
    const [picked, setPicked] = useState([]);                 // ordered ids
    const [music, setMusic] = useState("wedding");
    const [fmt, setFmt] = useState("vertical");
    const [secondsPerItem, setSecondsPerItem] = useState(3);
    const [captionMode, setCaptionMode] = useState("ai");
    const [manualCaption, setManualCaption] = useState("");
    const [reel, setReel] = useState(null);
    const [working, setWorking] = useState(false);
    const [copiedCaption, setCopiedCaption] = useState(false);

    useEffect(() => {
        if (!open) {
            setPicked([]); setReel(null); setWorking(false);
            setCopiedCaption(false); setManualCaption(""); setCaptionMode("ai");
        }
    }, [open]);

    // Only show items that have a real (>5KB) AI image
    const eligible = useMemo(
        () => sarees.filter((s) => {
            const ai = s.ai_model_images?.[0];
            if (!ai) return false;
            // Conservative client-side size estimate: base64 length must be > 6700 chars (~5KB)
            return typeof ai === "string" && ai.length > 6700;
        }),
        [sarees],
    );

    const togglePick = (id) => {
        setPicked((prev) => {
            if (prev.includes(id)) return prev.filter((x) => x !== id);
            if (prev.length >= 10) {
                toast.error("Max 10 items per reel");
                return prev;
            }
            return [...prev, id];
        });
    };

    const generate = async () => {
        if (picked.length < 2) {
            toast.error("Pick at least 2 items");
            return;
        }
        setWorking(true); setReel(null);
        try {
            const { data } = await api.post("/admin/reels/collection", {
                saree_ids: picked,
                music, fmt,
                seconds_per_item: secondsPerItem,
                caption_mode: captionMode,
                manual_caption: manualCaption,
            });
            const start = Date.now();
            while (Date.now() - start < 120_000) {
                await new Promise((r) => setTimeout(r, 2500));
                const { data: r } = await api.get(`/admin/reels/${data.id}`);
                if (r.status === "done") {
                    setReel(r);
                    toast.success("Collection reel ready!");
                    return;
                }
                if (r.status === "failed") {
                    toast.error(r.error || "Reel generation failed");
                    return;
                }
            }
            toast.error("Reel took too long — please retry");
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to start reel");
        } finally {
            setWorking(false);
        }
    };

    const downloadUrl = reel ? `${API}/admin/reels/${reel.id}/download` : null;

    const downloadFile = async () => {
        if (!reel) return;
        try {
            const token = getStoredToken();
            const res = await fetch(downloadUrl, {
                credentials: "include",
                headers: token ? { Authorization: `Bearer ${token}` } : {},
            });
            const blob = await res.blob();
            const a = document.createElement("a");
            a.href = URL.createObjectURL(blob);
            a.download = `vastra-collection-${reel.id.slice(0, 8)}.mp4`;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(a.href);
        } catch {
            toast.error("Could not download — try again");
        }
    };

    const shareWithFile = async (fallbackUrl) => {
        if (reel?.caption) { try { await navigator.clipboard.writeText(reel.caption); } catch {} }
        try {
            const token = getStoredToken();
            const res = await fetch(downloadUrl, {
                credentials: "include",
                headers: token ? { Authorization: `Bearer ${token}` } : {},
            });
            const blob = await res.blob();
            const file = new File([blob], `reel-${reel.id.slice(0, 8)}.mp4`, { type: "video/mp4" });
            if (navigator.canShare && navigator.canShare({ files: [file] })) {
                await navigator.share({ files: [file], text: reel.caption || "" });
                return;
            }
        } catch {}
        await downloadFile();
        if (fallbackUrl) window.open(fallbackUrl, "_blank");
    };

    const copyCaption = async () => {
        if (!reel?.caption) return;
        try {
            await navigator.clipboard.writeText(reel.caption);
            setCopiedCaption(true);
            toast.success("Caption copied");
            setTimeout(() => setCopiedCaption(false), 1500);
        } catch { toast.error("Could not copy"); }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="bg-brand-paper border-brand-border max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="collection-reel-dialog">
                <DialogHeader>
                    <DialogTitle className="font-serif text-2xl flex items-center gap-2">
                        <Film className="w-5 h-5 text-brand-primary" strokeWidth={1.5} />
                        Make a collection reel
                    </DialogTitle>
                </DialogHeader>

                {!reel ? (
                    <div className="mt-5 space-y-5">
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <label className="ayraa-overline">Pick items <span className="text-brand-soft text-[10px] tracking-normal">(2–10)</span></label>
                                <span className="text-xs text-brand-muted">{picked.length} selected</span>
                            </div>
                            {eligible.length === 0 ? (
                                <div className="text-sm text-brand-muted ayraa-card p-4">
                                    No items with an AI model photo yet. Use Magic Catalog on a few items first.
                                </div>
                            ) : (
                                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-[280px] overflow-y-auto p-1">
                                    {eligible.map((s) => {
                                        const idx = picked.indexOf(s.id);
                                        const sel = idx >= 0;
                                        return (
                                            <button
                                                key={s.id}
                                                type="button"
                                                onClick={() => togglePick(s.id)}
                                                className={`relative aspect-[3/4] rounded-md overflow-hidden border-2 transition ${sel ? "border-brand-primary ring-2 ring-brand-primary/30" : "border-transparent hover:border-brand-border"}`}
                                                data-testid={`coll-pick-${s.id}`}
                                            >
                                                <img src={s.ai_model_images[0]} alt={s.title} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                                                {sel ? (
                                                    <span className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-brand-primary text-white text-xs font-medium grid place-items-center">{idx + 1}</span>
                                                ) : null}
                                                <span className="absolute bottom-0 inset-x-0 px-1.5 py-1 text-[10px] text-white bg-gradient-to-t from-black/70 to-transparent line-clamp-1">{s.title}</span>
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                            <div>
                                <label className="ayraa-overline">Music</label>
                                <select className="ayraa-input mt-1.5" value={music} onChange={(e) => setMusic(e.target.value)} data-testid="coll-music-select">
                                    {MUSIC_OPTIONS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="ayraa-overline">Format</label>
                                <select className="ayraa-input mt-1.5" value={fmt} onChange={(e) => setFmt(e.target.value)} data-testid="coll-fmt-select">
                                    <option value="vertical">9:16 (Reels)</option>
                                    <option value="square">1:1 (Feed)</option>
                                </select>
                            </div>
                            <div>
                                <label className="ayraa-overline">Pace</label>
                                <select className="ayraa-input mt-1.5" value={secondsPerItem} onChange={(e) => setSecondsPerItem(parseFloat(e.target.value))} data-testid="coll-pace-select">
                                    <option value={2}>Fast · 2s/item</option>
                                    <option value={3}>Standard · 3s/item</option>
                                    <option value={4}>Slow · 4s/item</option>
                                </select>
                            </div>
                        </div>

                        <div>
                            <label className="ayraa-overline">Caption</label>
                            <div className="flex gap-2 mt-1.5">
                                <button type="button" className={`px-3 py-1.5 text-xs rounded-md border ${captionMode === "ai" ? "bg-brand-primary text-white border-brand-primary" : "bg-brand-paper border-brand-border"}`} onClick={() => setCaptionMode("ai")} data-testid="coll-caption-ai">
                                    <Sparkles className="w-3 h-3 inline mr-1" /> AI auto
                                </button>
                                <button type="button" className={`px-3 py-1.5 text-xs rounded-md border ${captionMode === "manual" ? "bg-brand-primary text-white border-brand-primary" : "bg-brand-paper border-brand-border"}`} onClick={() => setCaptionMode("manual")} data-testid="coll-caption-manual">
                                    Type my own
                                </button>
                            </div>
                            {captionMode === "manual" ? (
                                <textarea className="ayraa-input mt-2 min-h-[80px]" placeholder="Caption + hashtags…" value={manualCaption} onChange={(e) => setManualCaption(e.target.value)} data-testid="coll-manual-caption" />
                            ) : null}
                        </div>

                        <button
                            className="ayraa-btn-primary w-full"
                            disabled={working || picked.length < 2}
                            onClick={generate}
                            data-testid="coll-generate-btn"
                        >
                            {working ? (
                                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating reel ({picked.length * secondsPerItem}s)…</>
                            ) : (
                                <><Sparkles className="w-4 h-4 mr-2" /> Build reel from {picked.length || "?"} item{picked.length === 1 ? "" : "s"}</>
                            )}
                        </button>
                    </div>
                ) : (
                    <div className="mt-5 space-y-4" data-testid="coll-reel-preview">
                        <video controls autoPlay playsInline className="w-full rounded-md bg-black aspect-[9/16] sm:aspect-video" src={`${downloadUrl}?token=${encodeURIComponent(getStoredToken() || "")}`}>
                            <source src={`${downloadUrl}?token=${encodeURIComponent(getStoredToken() || "")}`} type="video/mp4" />
                        </video>

                        <div className="ayraa-card p-3">
                            <div className="flex items-start justify-between gap-2">
                                <div className="text-xs text-brand-soft uppercase tracking-wider">Caption</div>
                                <button onClick={copyCaption} className="text-xs text-brand-primary flex items-center gap-1" data-testid="coll-copy-caption">
                                    {copiedCaption ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                                    {copiedCaption ? "Copied" : "Copy"}
                                </button>
                            </div>
                            <p className="text-sm whitespace-pre-wrap mt-1">{reel.caption}</p>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                            <button onClick={() => shareWithFile("https://www.instagram.com/")} className="ayraa-btn-primary flex-1" data-testid="coll-share-ig">
                                <Share2 className="w-4 h-4 mr-1.5" /> Open in Instagram
                            </button>
                            <button onClick={() => shareWithFile(`https://wa.me/?text=${encodeURIComponent(reel?.caption || "")}`)} className="ayraa-btn-ghost flex-1" data-testid="coll-share-wa">
                                <Share2 className="w-4 h-4 mr-1.5" /> WhatsApp
                            </button>
                            <button onClick={downloadFile} className="ayraa-btn-ghost flex-1" data-testid="coll-download">
                                <Download className="w-4 h-4 mr-1.5" /> Download
                            </button>
                        </div>

                        <button className="text-xs text-brand-muted hover:text-brand-primary mx-auto block" onClick={() => setReel(null)} data-testid="coll-make-another">
                            ← Tweak settings &amp; regenerate
                        </button>
                    </div>
                )}

                <div className="mt-4 text-xs text-brand-soft flex items-start gap-1.5">
                    <AlertCircle className="w-3 h-3 shrink-0 mt-0.5" />
                    <span>Each AI photo plays for {secondsPerItem}s. Total reel = {picked.length || 0} × {secondsPerItem}s.</span>
                </div>
            </DialogContent>
        </Dialog>
    );
}
